// frontend/src/components/dashboard/ServicesManagement.js
import React, { useState, useEffect } from 'react';
import {
   Box,
   Paper,
   Table,
   TableBody,
   TableCell,
   TableContainer,
   TableHead,
   TableRow,
   Typography,
   Button,
   IconButton,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   TextField,
   Tooltip,
   Select,
   MenuItem,
   FormControl,
   InputLabel
} from '@mui/material';
import {
   Add as AddIcon,
   Edit as EditIcon,
   Delete as DeleteIcon
} from '@mui/icons-material';
import axiosInstance from '../../utils/axios';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../hooks/useSettings';

const ServiceDialog = ({ open, onClose, service, onSave }) => {
   const { t } = useTranslation();
   const [formData, setFormData] = useState({
       name: '',
       category: '',
       duration: '',
       price: '',
       maxprice: '',
   });

   useEffect(() => {
       if (service) {
           setFormData({
               name: service.name || '',
               category: service.category || '',
               duration: service.duration || '',
               price: service.price || '',
               maxPrice: service.maxPrice || ''
           });
       } else {
           setFormData({
               name: '',
               category: '',
               duration: '',
               price: ''
           });
       }
   }, [service]);

   const handleSubmit = async (e) => {
       e.preventDefault();
       onSave(formData);
   };

   return (
       <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
           <DialogTitle>
               {service ? t('common.edit') : t('common.add')}
           </DialogTitle>
           <form onSubmit={handleSubmit}>
               <DialogContent>
                   <TextField
                       fullWidth
                       label={t('common.nazvanie')}
                       value={formData.name}
                       onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                       margin="normal"
                       required
                   />
                   <TextField
                       fullWidth
                       label={t('common.category')}
                       value={formData.category}
                       onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                       margin="normal"
                       required
                   />
                   <TextField
                       fullWidth
                       label={t('common.duration')}
                       value={formData.duration}
                       onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                       margin="normal"
                       type="number"
                       required
                   />
                   <TextField
                       fullWidth
                       label={t('common.price')}
                       value={formData.price}
                       onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                       margin="normal"
                       type="number"
                       required
                   />
                   <TextField
    fullWidth
    label={t('common.maxprice')}
    value={formData.maxPrice}
    onChange={(e) => setFormData({ ...formData, maxPrice: e.target.value })}
    margin="normal"
    type="number"
/>
               </DialogContent>
               <DialogActions>
                   <Button onClick={onClose}>{t('common.cancel')}</Button>
                   <Button type="submit" variant="contained" color="primary">
                       {service ? t('common.save') : t('common.create')}
                   </Button>
               </DialogActions>
           </form>
       </Dialog>
   );
};

export default function ServicesManagement() {
   const { t } = useTranslation();
   const { formatPrice } = useSettings();
   const [services, setServices] = useState([]);
   const [selectedService, setSelectedService] = useState(null);
   const [openDialog, setOpenDialog] = useState(false);
   
   useEffect(() => {
       fetchServices();
   }, []);

   const fetchServices = async () => {
       try {
           const response = await axiosInstance.get('/services');
           setServices(response.data);
       } catch (error) {
           console.error('Error fetching services:', error);
       }
   };

   const handleSave = async (formData) => {
       try {
           if (selectedService) {
               await axiosInstance.patch(`/services/${selectedService._id}`, formData);
           } else {
               await axiosInstance.post('/services', formData);
           }
           fetchServices();
           setOpenDialog(false);
           setSelectedService(null);
       } catch (error) {
           console.error('Error saving service:', error);
       }
   };

   const handleDelete = async (serviceId) => {
       if (window.confirm(t('common.deleteConfirm'))) {
           try {
               await axiosInstance.delete(`/services/${serviceId}`);
               fetchServices();
           } catch (error) {
               console.error('Error deleting service:', error);
               alert(t('common.deleteError'));
           }
       }
   };

   const groupedServices = services.reduce((acc, service) => {
       const category = service.category || t('common.uncategorized');
       if (!acc[category]) {
           acc[category] = [];
       }
       acc[category].push(service);
       return acc;
   }, {});

   return (
       <Box>
           <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
               <Typography variant="h6">{t('common.title')}</Typography>
               <Button
                   variant="contained"
                   startIcon={<AddIcon />}
                   onClick={() => {
                       setSelectedService(null);
                       setOpenDialog(true);
                   }}
               >
                   {t('common.addnew')}
               </Button>
           </Box>

           {Object.entries(groupedServices).map(([category, categoryServices]) => (
               <Box key={category} sx={{ mb: 4 }}>
                   <Typography variant="h6" sx={{ mb: 2, bgcolor: 'background.paper', p: 1, borderRadius: 1 }}>
                       {category}
                   </Typography>
                   <TableContainer component={Paper}>
                       <Table>
                           <TableHead>
                               <TableRow>
                                   <TableCell>{t('common.nazvanie')}</TableCell>
                                   <TableCell>{t('common.duration')}</TableCell>
                                   <TableCell>{t('common.price')}</TableCell>
                                   <TableCell>{t('common.maxprice')}</TableCell>
                                   <TableCell>{t('common.actions')}</TableCell>
                               </TableRow>
                           </TableHead>
                           <TableBody>
                               {categoryServices.map((service) => (
                                   <TableRow key={service._id}>
                                       <TableCell>{service.name}</TableCell>
                                       <TableCell>{service.duration}</TableCell>
                                       <TableCell>{formatPrice(service.price)}</TableCell>
                                       <TableCell>{formatPrice(service.maxprice)}</TableCell>

                                       <TableCell>
                                           <Tooltip title={t('common.edit')}>
                                               <IconButton
                                                   size="small"
                                                   onClick={() => {
                                                       setSelectedService(service);
                                                       setOpenDialog(true);
                                                   }}
                                               >
                                                   <EditIcon fontSize="small" />
                                               </IconButton>
                                           </Tooltip>
                                           <Tooltip title={t('common.delete')}>
                                               <IconButton
                                                   size="small"
                                                   color="error"
                                                   onClick={() => handleDelete(service._id)}
                                               >
                                                   <DeleteIcon fontSize="small" />
                                               </IconButton>
                                           </Tooltip>
                                       </TableCell>
                                   </TableRow>
                               ))}
                           </TableBody>
                       </Table>
                   </TableContainer>
               </Box>
           ))}

           <ServiceDialog
               open={openDialog}
               onClose={() => {
                   setOpenDialog(false);
                   setSelectedService(null);
               }}
               service={selectedService}
               onSave={handleSave}
           />
       </Box>
   );
}